import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { useCreateResource, useUpdateResource } from "../hooks/useRepository";
import type { Resource, ResourceCategory, ResourceType } from "../types";
import { RESOURCE_TYPE_LABELS } from "../types";

interface Props {
  categories: ResourceCategory[];
  defaultCategoryId?: string;
  resource?: Resource; // provided = edit mode
  onClose: () => void;
}

const TYPES: ResourceType[] = ["document", "link", "contractor_document"];

export function ResourceFormModal({ categories, defaultCategoryId, resource, onClose }: Props) {
  const { mutateAsync: create, isPending: creating } = useCreateResource();
  const { mutateAsync: update, isPending: updating } = useUpdateResource();

  const [title, setTitle] = useState(resource?.title ?? "");
  const [description, setDescription] = useState(resource?.description ?? "");
  const [type, setType] = useState<ResourceType>(resource?.resource_type ?? "document");
  const [categoryId, setCategoryId] = useState(resource?.category_id ?? defaultCategoryId ?? "");
  const [url, setUrl] = useState(resource?.url ?? "");

  const isPending = creating || updating;
  const isLink = type === "link";
  const isEdit = !!resource;
  // A resource can only live in a category the user can manage. When editing, keep
  // the current category available even if the user can't manage it (read-only move).
  const selectableCategories = categories.filter(
    (c) => c.can_manage || (isEdit && c.id === resource?.category_id),
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !categoryId) return;

    if (isEdit) {
      await update({
        id: resource.id,
        data: { title, description, category: categoryId, url: isLink ? url : "" },
      });
    } else {
      await create({
        title,
        description,
        resource_type: type,
        category: categoryId,
        url: isLink ? url : "",
      });
    }
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            {isEdit ? "Edytuj zasób" : "Dodaj zasób"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4">
          {/* Type — only in create mode */}
          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Typ zasobu
              </label>
              <div className="flex gap-2">
                {TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={`flex-1 text-sm py-1.5 rounded-lg border transition-colors ${
                      type === t
                        ? "border-[var(--color-primary)] bg-blue-50 dark:bg-blue-900/30 text-[var(--color-primary)] dark:text-blue-300 font-medium"
                        : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300"
                    }`}
                  >
                    {RESOURCE_TYPE_LABELS[t]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Tytuł <span className="text-red-500">*</span>
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Kategoria <span className="text-red-500">*</span>
            </label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              required
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="">— wybierz kategorię —</option>
              {selectableCategories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* URL — only for link type */}
          {isLink && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                URL <span className="text-red-500">*</span>
              </label>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                type="url"
                required
                placeholder="https://..."
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Opis
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
            >
              {isPending && <Loader2 size={13} className="animate-spin" />}
              {isEdit ? "Zapisz" : "Dodaj"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
